import { planExit } from "./exit";
import { HELD } from "./store";
import { heldMessage, scopeLabel } from "./labels";

// The names of the rows in the warnings
const LABELS = {
  blocks: "Page",
  endpoints: "Endpoint",
  properties: "Property"
};

// Why a stash was refused, for the user: the store's error names the row
// (error.accessor, error.id). form: an entry of stashOpenForms' forms.
// "your changes can't be kept while ..." (e.g. after "You stay on this
// page:", or before the copy of the open variant).
export function refusalReason({ accessor, id, error }, propertyTitle) {
  const row = {
    accessor: error?.accessor ?? accessor,
    id: error?.id ?? id
  };
  const label = LABELS[row.accessor] ?? row.accessor;

  if (error?.code === HELD) {
    return heldMessage(
      { ...row, label },
      scopeLabel(error.scope ?? "", propertyTitle)
    );
  }

  const message = String(error?.message ?? error ?? "");
  if (message.includes("open Item form")) {
    return `your changes can't be kept while ${label} ${row.id} has changes in another form (e.g. the endpoint params). Save or cancel them first.`;
  }
  return `your changes can't be kept${message ? ` (${message})` : ""}.`;
}

const refusalWarning = (form, propertyTitle) =>
  `You stay on this page: ${refusalReason(form, propertyTitle)}`;

// What a navigation does (useConfirmNavigation), once the open forms were
// asked to put their changes into the draft (draft.stashOpenForms):
//
// - stash: the result of stashOpenForms ({ outcome, forms }), or null when
//   they were not asked (the form is not modified, no navigation, every
//   draft left, main);
// - the rest: the inputs of planExit (formScope, draftsLeft, navigating,
//   isDirty, propertyTitle), with draftsLeft read AFTER the stash.
//
// Returns { action, formStashed, discard, message, warnings }:
// - action "go": navigate; "ask": ask to leave without saving (discard the
//   drafts of `discard` on yes, message names them); "block": stay, tell
//   the user why (warnings);
// - formStashed: the modified form is in the draft now (or was clean): the
//   unsaved status of the form is cleared, the form is not asked about.
//
// The outcomes: "stashed" or "none" go on as for a clean form (other drafts
// the destination leaves still ask); "invalid" asks as before (the form's
// changes are lost on yes); "refused" stays. A form that was not asked (no
// form registered a stash for a scope the destination stays in: forms
// empty) is a modified form as before.
export function planNavigation({ formDirty = false, stash = null, ...exit }) {
  const asked = formDirty && stash?.forms?.length > 0;

  if (asked && stash.outcome === "refused") {
    return {
      action: "block",
      formStashed: false,
      discard: [],
      message: undefined,
      warnings: stash.forms
        .filter((form) => form.outcome === "refused")
        .map((form) => refusalWarning(form, exit.propertyTitle))
    };
  }

  const formStashed =
    asked && (stash.outcome === "stashed" || stash.outcome === "none");
  const plan = planExit({ ...exit, formDirty: formDirty && !formStashed });

  return {
    action: plan.ask ? "ask" : "go",
    formStashed,
    discard: plan.discard,
    message: plan.message,
    warnings: []
  };
}
